import client from '../client';

export interface ReturnTag {
  Id: number;
  Name: string;
}

export interface ReturnAuthorization {
  Id: number;
  OrderId: number;
  AuthorizationNumber: string;
  ReturnType: number; // 1 = Repair, 2 = Return
  ReturnTag: string; // Tag name as string
  Description: string;
  RecipientName: string;
  EmailAddress: string;
  PhoneNumber: string;
  CellNumber: string;
  SmsAlerts: boolean;
  CreateDate: string;
  CreateBy: string;
  CompleteDate?: string;
  CompleteBy?: string;
  ReceiveDate?: string;
  ReceiveBy?: string;
  HasReturnPackage: boolean;
  RepairComments: string;
  ReturnBinNumber: string;
  CreditMemo?: string;
  // Bill-to information from Order
  BillToName?: string;
  BillToAddress1?: string;
  BillToAddress2?: string;
  BillToCity?: string;
  BillToState?: string;
  BillToZipCode?: string;
  BillToCountry?: string;
  // Ship-to information from Order
  ShipToName?: string;
  ShipToAddress1?: string;
  ShipToAddress2?: string;
  ShipToCity?: string;
  ShipToState?: string;
  ShipToZipCode?: string;
  ShipToCountry?: string;
}

export interface ReturnPart {
  Id: number;
  ReturnId: number;
  PartNumber: string;
  Description?: string;
  IsRepair: boolean;
  Received: boolean;
  Condition?: number;
  ConditionDescription?: string;
}

export interface ReturnComment {
  Id: number;
  ReturnId: number;
  Description: string;
  CreateDate: string;
  CreateBy: string;
}

export interface ReturnImage {
  Id: number;
  ReturnId: number;
  FileName: string;
  OriginalFileName: string;
  FilePath: string;
  FileSize: number;
  MimeType: string;
  UploadedBy: string;
  UploadedDate: string;
}

export interface CreateReturnDTO {
  orderId: number;
  recipientName?: string;
  recipientPhone?: string;
  recipientEmail?: string;
  description: string;
  returnTagId: number;
  returnType: 'Repair' | 'Return';
  hasReturnPackage: boolean;
  partNumbers: string[];
  createdBy: string;
}

export interface ReturnWithDetails extends ReturnAuthorization {
  parts: ReturnPart[];
  comments: ReturnComment[];
}

class ReturnService {
  /**
   * Get all return tags
   */
  async getReturnTags(): Promise<ReturnTag[]> {
    const response: any = await client.get('/returns/tags');
    return response.data;
  }

  /**
   * Get return by ID with parts and comments
   */
  async getReturnById(id: number): Promise<ReturnWithDetails> {
    const response: any = await client.get(`/returns/${id}`);
    return response.data;
  }

  /**
   * Get returns by order ID
   */
  async getReturnsByOrderId(orderId: number): Promise<ReturnAuthorization[]> {
    const response: any = await client.get(`/returns/order/${orderId}`);
    return response.data;
  }

  /**
   * Create a new return authorization
   */
  async createReturn(data: CreateReturnDTO): Promise<ReturnAuthorization> {
    const response: any = await client.post('/returns', data);
    return response.data;
  }

  /**
   * Add comment to a return
   */
  async addComment(returnId: number, comment: string, createdBy: string): Promise<ReturnComment> {
    const response: any = await client.post(`/returns/${returnId}/comments`, {
      comment,
      createdBy
    });
    return response.data;
  }

  /**
   * Update a comment
   */
  async updateComment(commentId: number, description: string): Promise<void> {
    await client.put(`/returns/comments/${commentId}`, { description });
  }

  /**
   * Delete a comment
   */
  async deleteComment(commentId: number): Promise<void> {
    await client.delete(`/returns/comments/${commentId}`);
  }

  /**
   * Mark return as received
   */
  async markAsReceived(returnId: number, receivedBy: string): Promise<void> {
    await client.post(`/returns/${returnId}/received`, { receivedBy });
  }

  /**
   * Process return as received with condition
   * - passed: Add parts to stock with bin number
   * - not_sellable: Add note to RGA
   */
  async processReceived(returnId: number, data: {
    condition: 'passed' | 'not_sellable';
    binNumber?: string;
    note?: string;
    receivedBy: string;
  }): Promise<void> {
    await client.post(`/returns/${returnId}/process-received`, data);
  }

  /**
   * Mark return as completed
   */
  async markAsCompleted(returnId: number, completedBy: string): Promise<void> {
    await client.post(`/returns/${returnId}/completed`, { completedBy });
  }

  /**
   * Update credit memo number
   */
  async updateCreditMemo(returnId: number, creditMemo: string): Promise<void> {
    await client.put(`/returns/${returnId}/credit-memo`, { creditMemo });
  }

  /**
   * Update the return's Ship-To address (does not change the order)
   */
  async updateShipTo(
    returnId: number,
    address: {
      ShipToName: string;
      ShipToAddress1: string;
      ShipToAddress2: string;
      ShipToCity: string;
      ShipToState: string;
      ShipToZipCode: string;
      ShipToCountry: string;
    }
  ): Promise<void> {
    await client.put(`/returns/${returnId}/ship-to`, address);
  }

  /**
   * Update return description/reason
   */
  async updateDescription(returnId: number, description: string): Promise<void> {
    await client.put(`/returns/${returnId}/description`, { description });
  }

  /**
   * Update return type (1 = Repair, 2 = Return)
   */
  async updateReturnType(returnId: number, returnType: number): Promise<void> {
    await client.put(`/returns/${returnId}/type`, { returnType });
  }

  /**
   * Update contact information (RecipientName, EmailAddress, PhoneNumber)
   */
  async updateContactInfo(returnId: number, contactInfo: { recipientName: string; emailAddress: string; phoneNumber: string }): Promise<void> {
    await client.put(`/returns/${returnId}/contact`, contactInfo);
  }

  /**
   * Delete a return authorization
   */
  async deleteReturn(returnId: number): Promise<void> {
    await client.delete(`/returns/${returnId}`);
  }

  /**
   * Update a return part's part number
   */
  async updateReturnPart(partId: number, partNumber: string): Promise<void> {
    await client.put(`/returns/parts/${partId}`, { partNumber });
  }

  /**
   * Get order items for a return (to populate part number dropdown)
   */
  async getOrderItems(returnId: number): Promise<{ PartNumber: string; Description: string }[]> {
    const response: any = await client.get(`/returns/${returnId}/order-items`);
    return response.data;
  }

  /**
   * Bulk update return parts - sync selected part numbers with the return
   */
  async bulkUpdateParts(returnId: number, partNumbers: string[]): Promise<void> {
    await client.put(`/returns/${returnId}/parts`, { partNumbers });
  }

  /**
   * Start repair - sends notification email to customer and marks as In Progress
   */
  async startRepair(returnId: number, startedBy: string): Promise<void> {
    await client.post(`/returns/${returnId}/start-repair`, { startedBy });
  }

  /**
   * Ship repair/replacement - records shipment, marks as completed, optionally sends email
   */
  async shipRepair(returnId: number, data: {
    shippingCompany: string;
    trackingNumber: string;
    comments: string;
    sendCustomerEmail: boolean;
    completedBy: string;
  }): Promise<void> {
    await client.post(`/returns/${returnId}/ship`, data);
  }

  /**
   * Search returns with filters
   */
  async searchReturns(filters: {
    status?: 'open' | 'completed';
    orderId?: number;
    authorizationNumber?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ returns: ReturnAuthorization[]; total: number }> {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.orderId) params.append('orderId', filters.orderId.toString());
    if (filters.authorizationNumber) params.append('authorizationNumber', filters.authorizationNumber);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

    const response: any = await client.get(`/returns?${params.toString()}`);
    // After smart-unwrap the body is the returns array (backend puts array in `data` field)
    const data = response.data;
    const returns: ReturnAuthorization[] = Array.isArray(data) ? data : (data?.data ?? []);
    return {
      returns,
      total: returns.length
    };
  }

  /**
   * Get images for a return
   */
  async getReturnImages(returnId: number): Promise<ReturnImage[]> {
    const response: any = await client.get(`/returns/${returnId}/images`);
    return response.data;
  }

  /**
   * Upload an image to a return
   */
  async uploadImage(returnId: number, file: File, uploadedBy: string): Promise<ReturnImage> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('uploadedBy', uploadedBy);

    const response: any = await client.post(`/returns/${returnId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Get the URL for an image file
   */
  getImageUrl(imageId: number): string {
    return `/api/v1/returns/images/${imageId}/file`;
  }

  /**
   * Delete an image
   */
  async deleteImage(imageId: number): Promise<void> {
    await client.delete(`/returns/images/${imageId}`);
  }
}

export const returnService = new ReturnService();
